import { axiosPrivate } from "../../API/API";
import { useEffect, useRef } from "react";
import useRefreshToken from "./useRefreshToken";
import useAuth from "./useAuth";
import { clearAuthStorage } from "../../utils/authRefresh";

const useAxiosPrivate = () => {
  const refresh = useRefreshToken();
  const { auth, setAuth } = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;

  useEffect(() => {
    const requestIntercept = axiosPrivate.interceptors.request.use(
      (config) => {
        const token = authRef.current?.accessToken;
        if (!config.headers["Authorization"] && token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseIntercept = axiosPrivate.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;
        const url = prevRequest?.url || "";

        if (url.includes("auth/token/refresh")) {
          return Promise.reject(error);
        }

        if (error?.response?.status === 401 && prevRequest && !prevRequest._retry) {
          prevRequest._retry = true;
          try {
            const newAccessToken = await refresh();
            prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            return axiosPrivate(prevRequest);
          } catch {
            clearAuthStorage();
            setAuth({});
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axiosPrivate.interceptors.request.eject(requestIntercept);
      axiosPrivate.interceptors.response.eject(responseIntercept);
    };
  }, [refresh, setAuth]);

  return axiosPrivate;
};

export default useAxiosPrivate;
